import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export interface AdminProfile {
  id: string
  profile_id: string
  canonical_id: string
  tenant_subject_id: string | null
  tenant_id: string | null
  tenant_name: string | null
  roscoe_user_id: number | null
  coverage_level: string
  stability_indicator: string
  total_observation_count: number
  last_distillation: string | null
  is_active: boolean
  is_archived: boolean
  created_at: string
  updated_at: string
}

export interface ProfileDetail {
  id: string
  profile_id: string
  canonical_id: string
  tenant_id: string | null
  tenant_name: string | null
  tenant_subject_id: string | null
  coverage_level: string
  stability_indicator: string
  total_observation_count: number
  last_distillation: string | null
  is_active: boolean
  is_archived: boolean
  signals_universal: Record<string, any>
  signals_behavioral: Record<string, any>
  signals_regulated: Record<string, any>
  signals_pragmatic: Record<string, any>
  domain_preferences: Record<string, any> | null
  relationships_data: Record<string, any> | null
  domain_registry: Record<string, any> | null
  governance_data: Record<string, any> | null
  persona_references: Record<string, any> | null
  created_at: string
  updated_at: string | null
}

interface ProfileListResponse {
  profiles: AdminProfile[]
  total: number
  limit: number
  offset: number
}

interface UseAdminProfilesOptions {
  search?: string
  tenant_id?: string
  coverage_level?: string
  is_active?: boolean
  from_date?: string
  to_date?: string
  limit?: number
  offset?: number
}

// =============================================================================
// QUERY KEYS
// =============================================================================

const QUERY_KEYS = {
  profiles: (options: UseAdminProfilesOptions) => ['admin-profiles', options],
  profile: (profileId: string) => ['admin-profile', profileId],
}

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Fetch list of profiles for admin view
 */
export function useAdminProfiles(options: UseAdminProfilesOptions = {}) {
  const {
    search,
    tenant_id,
    coverage_level,
    is_active,
    from_date,
    to_date,
    limit = 50,
    offset = 0,
  } = options

  return useQuery<ProfileListResponse>({
    queryKey: QUERY_KEYS.profiles({ search, tenant_id, coverage_level, is_active, from_date, to_date, limit, offset }),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (tenant_id) params.append('tenant_id', tenant_id)
      if (coverage_level) params.append('coverage_level', coverage_level)
      if (is_active !== undefined) params.append('is_active', String(is_active))
      if (from_date) params.append('from_date', from_date)
      if (to_date) params.append('to_date', to_date)
      params.append('limit', String(limit))
      params.append('offset', String(offset))

      const res = await api.get(`/admin/mindmetal/profiles?${params.toString()}`)
      return res.data
    },
  })
}

/**
 * Fetch single profile details
 */
export function useAdminProfile(profileId: string | null) {
  return useQuery<ProfileDetail>({
    queryKey: QUERY_KEYS.profile(profileId || ''),
    queryFn: async () => {
      const res = await api.get(`/admin/mindmetal/profiles/${profileId}`)
      return res.data
    },
    enabled: !!profileId,
  })
}

// =============================================================================
// MUTATIONS
// =============================================================================

/**
 * Export profiles as JSON
 */
export function useExportProfiles() {
  return useMutation<Blob, Error, string[]>({
    mutationFn: async (profileIds: string[]) => {
      const res = await api.post(
        '/admin/mindmetal/profiles/export',
        profileIds,
        { responseType: 'blob' }
      )
      return res.data
    },
  })
}
